import { createContext, useCallback, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { api } from '../lib/api';
import type { Studio } from '../lib/types';

export const StudioContext = createContext<Studio | null>(null);

export function useStudio(): Studio {
  const s = useContext(StudioContext);
  if (!s) throw new Error('useStudio outside StudioLayout');
  return s;
}

export const studioQuery = (slug: string) => ({
  queryKey: ['studio', slug],
  queryFn: () => api.studio(slug),
  staleTime: 60_000,
});

export function useStudioQuery(slug: string) {
  return useQuery(studioQuery(slug));
}

/**
 * The booking flow lives in the URL (?book=service|time|contacts|done), so the
 * system Back button walks back through the steps and links can open it.
 */
export type BookStep = 'service' | 'time' | 'contacts' | 'done';

export function useBookingNav() {
  const [params, setParams] = useSearchParams();
  const step = (params.get('book') as BookStep | null) ?? null;
  const go = useCallback(
    (next: BookStep | null, extra: Record<string, string | null> = {}, replace = false) => {
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next) p.set('book', next);
          else ['book', 'service', 'day', 'at', 'bid'].forEach((k) => p.delete(k));
          for (const [k, v] of Object.entries(extra)) {
            if (v === null) p.delete(k);
            else p.set(k, v);
          }
          return p;
        },
        { replace },
      );
    },
    [setParams],
  );
  return {
    step,
    serviceId: params.get('service'),
    day: params.get('day'),
    at: params.get('at'),
    bookingId: params.get('bid'),
    open: (serviceId?: string) => go(serviceId ? 'time' : 'service', serviceId ? { service: serviceId } : {}),
    go,
    close: () => go(null, {}, true),
  };
}
